import { inject, observer } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { Instance } from 'mobx-state-tree';
import { TLCode } from '../../common/enums';
import { ColDef } from 'ag-grid-community';
import { DutyExchangeItem } from '../../store/models/DutyExchangeItem';
import BaseGridXL from '../common/grid/BaseGridXL';
import { Button, Checkbox, Colors, InputGroup, Intent, Spinner } from '@blueprintjs/core';

import {
  dateFilterNormal,
  dateTimeFilterNormal,
  renderGridDateTimeWithDetails,
  renderGridDateWithDay,
} from '../../store/utils';
import { AuthenticationStore, DutyExchangeStore, SystemStatusStore } from '../../store';
import { SystemStatus } from '../../store/models/SystemStatus';
import { TranslatedMessage } from '../common/translation';

interface ExchangeHistoryTabProps extends WithTranslateProps {
  dutyExchangeList: Array<Instance<typeof DutyExchangeItem>>;
  isLoading: boolean;
  exchangeHistoryIsLoading: boolean;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  DutyExchangeStore?: Instance<typeof DutyExchangeStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  OnCheckedPastDates?: () => void;
  isCheckBoxPastDateSelected?: boolean;
}

interface ExchangeHistoryTabState {
  filter: string;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  exportIsEmpty?: boolean;
}

const ButtonContainerLeft = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-start',
  marginBottom: 5,
});

const ButtonContainerRight = styled.div({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'flex-end',
  marginBottom: 5,
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 5px;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

@inject('SystemStatusStore')
@inject('DutyExchangeStore')
@inject('AuthenticationStore')
@observer
class ExchangeHistoryTab extends React.Component<ExchangeHistoryTabProps, ExchangeHistoryTabState> {
  state = {
    filter: '',
    checkBoxPastDateSelected: false,
    exportIsEmpty: false,
  } as ExchangeHistoryTabState;

  private getLastDutyExchangeItemId = () => {
    return this.state.systemStatus === undefined
      ? 0
      : ((this.state.systemStatus!.find((s) => s.subSystem === this.MaxExchangeItemIdSentSubSystem)!
          .status as unknown) as Number);
  };

  private getColumnDefs = (): ColDef[] => {
    const { translate } = this.props;
    return [
      {
        headerName: translate(TLCode.EXCHANGE_HISTORY_DutyDate),
        field: 'dutyDate',
        width: 130,
        cellRenderer: renderGridDateWithDay,
        filterParams: {
          textCustomComparator: dateFilterNormal,
        },
        sort: 'asc',
        sortedAt: 1,
      },
      {
        headerName: '', //translate(TLCode.EXCHANGE_HISTORY_DutyType),
        field: 'dutyType',
        width: 70,
        sort: 'asc',
        sortedAt: 2,
      },
      {
        headerName: translate(TLCode.EXCHANGE_HISTORY_Sender),
        field: 'sender',
        width: 300,
      },
      {
        headerName: translate(TLCode.EXCHANGE_HISTORY_Recipient),
        field: 'recipient',
        width: 300,
      },
      {
        headerName: translate(TLCode.EXCHANGE_HISTORY_Date),
        field: 'date',
        width: 150,
        minWidth: 150,
        cellRenderer: renderGridDateTimeWithDetails,
        filterParams: {
          textCustomComparator: dateTimeFilterNormal,
        },
      },
      {
        field: 'hasBeenDownloaded',
        sort: 'asc',
        sortedAt: 0,
        hide: true,
      },
    ];
  };

  private readonly MaxExchangeItemIdSentSubSystem = 'MaxExchangeItemIdSentToWebGarde';

  async componentDidMount() {
    await this.getSystemStatusMaxExchangeItemId(false);
  }

  buildGridData = () => {
    return this.props.dutyExchangeList.map((elem) => ({
      dutyExchangeItemId: elem.dutyExchangeItemId,
      date: elem.createdOn,
      sender: elem.sender.apbNumber + ' - ' + elem.sender.name,
      recipient: elem.recipient.apbNumber + ' - ' + elem.recipient.name,
      dutyDate: elem.dutyAssignment.duty ? elem.dutyAssignment.duty.dutyDate.date : '',
      dutyType: elem.dutyAssignment.duty ? elem.dutyAssignment.duty.dutyType!.name : '',
      hasBeenDownloaded: elem.dutyExchangeItemId <= this.getLastDutyExchangeItemId() ? true : false,
    }));
  };

  setFilterText = (filter) => {
    this.setState({
      filter,
    });
  };

  toggleColor = (color) => {
    if (color == Colors.WHITE) {
      return Colors.LIGHT_GRAY4;
    } else {
      return Colors.WHITE;
    }
  };

  private async getSystemStatusMaxExchangeItemId(exportEmpty) {
    const criteria: any = {
      subSystem: this.MaxExchangeItemIdSentSubSystem,
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    this.setState({ systemStatus } as ExchangeHistoryTabState);
    this.setState({ exportIsEmpty: exportEmpty });
  }

  render() {
    const { isLoading, exchangeHistoryIsLoading, OnCheckedPastDates, isCheckBoxPastDateSelected } = this.props;
    let backgroundColor = Colors.WHITE;
    let exportEmpty = false;
    const { translate } = this.props;
    const downloadDutyExchanges = async (data) => {
      exportEmpty = await this.props.DutyExchangeStore!.downloadLatestExchanges();
      this.getSystemStatusMaxExchangeItemId(exportEmpty);
      // this.setState({ exportIsEmpty: exportEmpty });
    };

    const checkBox = async () => {
      OnCheckedPastDates && (await OnCheckedPastDates());
      console.log('boxChecked!');
    };
    return (
      <Container>
        {exchangeHistoryIsLoading ? (
          <SpinnerContainer>
            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
          </SpinnerContainer>
        ) : (
          <>
            <DivLine>
              <ButtonContainerLeft>
                <InputGroup
                  placeholder="Filter..."
                  leftIcon="search"
                  value={this.state.filter}
                  onChange={(e) => this.setFilterText(e.target.value)}
                />
                &nbsp;&nbsp;
                <Checkbox
                  label={translate(TLCode.EXCHANGE_HISTORY_LoadPastDate)}
                  checked={isCheckBoxPastDateSelected}
                  onChange={(e) => checkBox()}
                />
              </ButtonContainerLeft>
              <ButtonContainerRight>
                <Button
                  intent="primary"
                  onClick={downloadDutyExchanges}
                  hidden={!(this.props.AuthenticationStore!.isSuperAdmin || this.props.AuthenticationStore!.isAdmin)}
                >
                  <TranslatedMessage id={TLCode.ROSTER_DownloadDutyExchangesButton} />
                </Button>
                {/* {!this.state.exportIsEmpty && <div>test</div>} */}
                {this.state.exportIsEmpty && (
                  <div style={{ backgroundColor: 'red', color: 'white', fontWeight: 'bold', marginLeft: '10px' }}>
                    &nbsp;Pas de données à exporter&nbsp;
                  </div>
                )}
              </ButtonContainerRight>
            </DivLine>
            <DivLine />
            <BaseGridXL
              keyName="dutyExchangeItemId"
              columnDefs={this.getColumnDefs()}
              rowData={this.buildGridData()}
              loading={isLoading}
              quickFilterText={this.state.filter}
              getRowStyle={(params) => {
                console.log(params);
                if (
                  params.data !== undefined &&
                  params.data.dutyExchangeItemId &&
                  (params.data.dutyExchangeItemId as Number) <= this.getLastDutyExchangeItemId()
                ) {
                  return { 'background-color': Colors.LIGHT_GRAY1 };
                }
                if (params.node.rowIndex == 0) {
                  return {
                    'background-color': backgroundColor,
                  };
                } else {
                  const prevDate = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1).data.dutyDate;
                  const prevType = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1).data.dutyType;
                  const prevSender = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1).data.sender;
                  const curDate = params.api.getDisplayedRowAtIndex(params.node.rowIndex).data.dutyDate;
                  const curSender = params.api.getDisplayedRowAtIndex(params.node.rowIndex).data.sender;
                  const curType = params.api.getDisplayedRowAtIndex(params.node.rowIndex).data.dutyType;
                  if (prevDate != curDate || prevSender != curSender || prevType == curType) {
                    // backgroundColor = this.toggleColor(backgroundColor);
                  }
                  return {
                    'background-color': backgroundColor,
                  };
                }
              }}
            />
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(ExchangeHistoryTab);
